import React, { useMemo } from 'react';
import { BarChart3, PieChart, Layers, Tag, Hash, Share2, CheckCircle2 } from 'lucide-react';
import { CampaignPost } from '../types/calendar';

interface AnalyticsViewProps {
  posts: CampaignPost[];
  onSelectPost?: (post: CampaignPost) => void;
}

export const AnalyticsView: React.FC<AnalyticsViewProps> = ({ posts }) => {
  const totalPosts = posts.length;

  // Platform Distribution
  const platformCounts = useMemo(() => {
    const map: Record<string, number> = {};
    posts.forEach(p => {
      const pl = p.platform || 'Unknown';
      map[pl] = (map[pl] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [posts]);

  // Format Distribution
  const formatCounts = useMemo(() => {
    const map: Record<string, number> = {};
    posts.forEach(p => {
      const f = p.format || 'Unspecified';
      map[f] = (map[f] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [posts]);

  // Phase Distribution
  const phaseCounts = useMemo(() => {
    const map: Record<string, number> = {};
    posts.forEach(p => {
      const ph = p.phase || 'Other';
      map[ph] = (map[ph] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [posts]);

  // Content Pillar Distribution
  const pillarCounts = useMemo(() => {
    const map: Record<string, number> = {};
    posts.forEach(p => {
      const pil = p.contentPillar || 'General';
      map[pil] = (map[pil] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [posts]);

  // Hashtag Analysis
  const topHashtags = useMemo(() => {
    const map: Record<string, number> = {};
    posts.forEach(p => {
      if (!p.hashtags) return;
      const tags = p.hashtags.match(/#[a-zA-Z0-9_]+/g);
      if (tags) {
        tags.forEach(tag => {
          const lower = tag.toLowerCase();
          map[lower] = (map[lower] || 0) + 1;
        });
      }
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 15);
  }, [posts]);

  const platformColors: Record<string, string> = {
    Instagram: '#e1306c',
    Facebook: '#1877f2',
    TikTok: '#00f2fe',
    Pinterest: '#e60023',
    YouTube: '#ff0000',
  };

  return (
    <div style={{
      padding: '1.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem',
      flex: 1,
    }}>
      {/* Top Stat KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
      }}>
        <div style={{
          background: 'var(--bg-surface)',
          padding: '1.25rem',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            background: 'rgba(99, 102, 241, 0.15)',
            color: 'var(--accent-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <BarChart3 size={24} />
          </div>
          <div>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Total Scheduled Posts</span>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', fontWeight: 800 }}>{totalPosts}</h3>
          </div>
        </div>

        <div style={{
          background: 'var(--bg-surface)',
          padding: '1.25rem',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            background: 'rgba(168, 85, 247, 0.15)',
            color: '#a855f7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Share2 size={24} />
          </div>
          <div>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Target Platforms</span>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', fontWeight: 800 }}>{platformCounts.length}</h3>
          </div>
        </div>

        <div style={{
          background: 'var(--bg-surface)',
          padding: '1.25rem',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            background: 'rgba(16, 185, 129, 0.15)',
            color: '#10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Layers size={24} />
          </div>
          <div>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Content Formats</span>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', fontWeight: 800 }}>{formatCounts.length}</h3>
          </div>
        </div>

        <div style={{
          background: 'var(--bg-surface)',
          padding: '1.25rem',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            background: 'rgba(245, 158, 11, 0.15)',
            color: '#f59e0b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Hash size={24} />
          </div>
          <div>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Unique Hashtags</span>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', fontWeight: 800 }}>{topHashtags.length}+</h3>
          </div>
        </div>
      </div>

      {/* Main Charts Row 1: Platform & Format Breakdown */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: '1.5rem',
      }}>
        {/* Platform Share Widget */}
        <div style={{
          background: 'var(--bg-surface)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.05rem', fontWeight: 700 }}>
              Posts by Platform
            </h3>
            <PieChart size={18} style={{ color: 'var(--text-light)' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {platformCounts.map(([platform, count]) => {
              const pct = Math.round((count / (totalPosts || 1)) * 100);
              const color = platformColors[platform] || 'var(--accent-primary)';

              return (
                <div key={platform} style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ fontWeight: 600 }}>{platform}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{count} ({pct}%)</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '10px',
                    borderRadius: '5px',
                    background: 'var(--bg-primary)',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: color,
                      borderRadius: '5px',
                      transition: 'width 0.5s ease',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Content Format Distribution Widget */}
        <div style={{
          background: 'var(--bg-surface)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.05rem', fontWeight: 700 }}>
              Content Format Distribution
            </h3>
            <Layers size={18} style={{ color: 'var(--text-light)' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {formatCounts.slice(0, 7).map(([fmt, count]) => {
              const pct = Math.round((count / (totalPosts || 1)) * 100);

              return (
                <div key={fmt} style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ fontWeight: 600 }}>{fmt}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{count} ({pct}%)</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '10px',
                    borderRadius: '5px',
                    background: 'var(--bg-primary)',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: 'var(--accent-primary)',
                      borderRadius: '5px',
                      transition: 'width 0.5s ease',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Charts Row 2: Campaign Phase & Top Hashtags */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: '1.5rem',
      }}>
        {/* Campaign Phase Breakdown */}
        <div style={{
          background: 'var(--bg-surface)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.05rem', fontWeight: 700 }}>
            Campaign Phase Distribution
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {phaseCounts.map(([phase, count]) => {
              const pct = Math.round((count / (totalPosts || 1)) * 100);

              return (
                <div key={phase} style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ fontWeight: 600 }}>{phase}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{count} ({pct}%)</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '10px',
                    borderRadius: '5px',
                    background: 'var(--bg-primary)',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: '#10b981',
                      borderRadius: '5px',
                      transition: 'width 0.5s ease',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Hashtags Cloud */}
        <div style={{
          background: 'var(--bg-surface)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.05rem', fontWeight: 700 }}>
            Top Hashtags Frequency
          </h3>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {topHashtags.map(([tag, count]) => (
              <span
                key={tag}
                style={{
                  padding: '0.35rem 0.65rem',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  fontSize: '0.8rem',
                  color: 'var(--accent-primary)',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                }}
              >
                {tag}
                <span style={{
                  fontSize: '0.7rem',
                  padding: '0.1rem 0.35rem',
                  borderRadius: '9999px',
                  background: 'var(--accent-glow)',
                }}>
                  {count}
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
